import React, { useCallback, useEffect, useRef, useState } from 'react';

import Cropper from 'react-easy-crop';
import type { Area, Point } from 'react-easy-crop';

import { Box, Slider, Stack, Typography } from '@mui/material';

const ASPECT_RATIO_MAP: Record<string, number> = {
  '16:9': 16 / 9,
  '4:5': 4 / 5,
  '9:16': 9 / 16,
};

const MIN_ZOOM = 0.3;
const MAX_ZOOM = 3;

export interface CropResult {
  x: number;
  y: number;
  zoom: number;
  croppedAreaPercentages: Area;
}

interface VideoCropPreviewProps {
  videoUrl: string;
  aspectRatio: string;
  startTime: number;
  clipDuration: number;
  onCropComplete: (result: CropResult) => void;
}

export default function VideoCropPreview({
  videoUrl,
  aspectRatio,
  startTime,
  clipDuration,
  onCropComplete,
}: VideoCropPreviewProps) {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const videoElRef = useRef<HTMLVideoElement | null>(null);

  const aspect = ASPECT_RATIO_MAP[aspectRatio] ?? 16 / 9;

  const handleVideoRef = useCallback((ref: React.RefObject<HTMLVideoElement>) => {
    videoElRef.current = ref.current;
  }, []);

  useEffect(() => {
    const video = videoElRef.current;
    if (!video) return;

    const endTime = startTime + clipDuration;

    const onTimeUpdate = () => {
      if (video.currentTime >= endTime || video.currentTime < startTime - 0.5) {
        video.currentTime = startTime;
      }
    };

    video.currentTime = startTime;
    video.addEventListener('timeupdate', onTimeUpdate);
    return () => video.removeEventListener('timeupdate', onTimeUpdate);
  }, [startTime, clipDuration]);

  const handleCropComplete = useCallback(
    (croppedArea: Area) => {
      onCropComplete({
        x: crop.x,
        y: crop.y,
        zoom,
        croppedAreaPercentages: croppedArea,
      });
    },
    [crop, zoom, onCropComplete],
  );

  const containerHeight = aspect >= 1 ? 240 : 360;

  return (
    <Box sx={{ width: 320, flexShrink: 0 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'center' }}>
        {aspectRatio}
      </Typography>

      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: containerHeight,
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'grey.900',
        }}
      >
        <Cropper
          video={videoUrl}
          crop={crop}
          zoom={zoom}
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          aspect={aspect}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={handleCropComplete}
          showGrid={true}
          objectFit="contain"
          setVideoRef={handleVideoRef}
          mediaProps={
            { muted: true, playsInline: true, crossOrigin: 'anonymous' } as React.VideoHTMLAttributes<HTMLVideoElement>
          }
        />
      </Box>

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1, px: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          Zoom
        </Typography>
        <Slider
          value={zoom}
          min={MIN_ZOOM}
          max={MAX_ZOOM}
          step={0.05}
          onChange={(_, value) => setZoom(value as number)}
          size="small"
          sx={{ flex: 1 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', minWidth: 32 }}>
          {zoom.toFixed(1)}x
        </Typography>
      </Stack>
    </Box>
  );
}
